import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {APP_INITIALIZER, importProvidersFrom, NgModule} from '@angular/core';
import {
  BrowserModule,
  provideClientHydration,
  withHttpTransferCacheOptions,
} from '@angular/platform-browser';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {StoreModule} from '@ngrx/store';
import {LaddaModule} from 'angular2-ladda';
import {ToastrModule} from 'ngx-toastr';
import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {headerChangedReducer} from './services/appstate/reducer/header.reducer';
import {ConstantService} from './services/constant.service';
import {CrudService} from './shared/crud/crud.service';
import {SharedService} from './shared/shared.service';
import {AuthInterceptor} from "./shared/interceptor/auth.interceptor";
import {AdminAuthCredentialsInterceptor} from "./shared/interceptor/admin-auth-credentials.interceptor";
import {NgZoneHttpEnsureCdInterceptor} from "./shared/interceptor/ng-zone-http-ensure-cd.interceptor";
import {AuthGuard} from "./shared/guards/auth.guard";
import {MfaEnforcementGuard} from "./shared/guards/mfa-enforcement.guard";
import {NgxPermissionsModule} from "ngx-permissions";
import {TranslateLoader, TranslateModule, TranslatePipe} from "@ngx-translate/core";
import {CustomRouterService} from "./services/custom-router.service";
import {FormsModule} from "@angular/forms";
import {TranslationService} from "./shared/translation/translation.service";
import {createCustomTranslateLoader} from "./models/translator";
import {HttpNoInterceptorsService} from "./shared/translation/http-no-interceptors.service";
import {SharedModule} from "./shared/shared.module";
import {AdminPermissionBootstrapService} from "./services/admin-permission-bootstrap.service";

export function adminPortalPermissionsInit(
  bootstrap: AdminPermissionBootstrapService,
): () => void {
  return () => bootstrap.hydrateFromCachedSession();
}

@NgModule({
  declarations: [
    AppComponent
  ],
  imports: [
    // BrowserModule must be root. SSR builds (`main.server.ts`): `provideClientHydration` reuses server DOM on the client.
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    SharedModule,
    ToastrModule.forRoot(),
    LaddaModule.forRoot({
      spinnerColor: "#C0C0C0",
      spinnerSize: 30,
      style: "zoom-out",
    }),
    StoreModule.forRoot(
      {
        headerChanged: headerChangedReducer,
      },
      {
        runtimeChecks: {
          strictStateImmutability: false,
          strictActionImmutability: false,
        },
      }
    ),
    FormsModule,
    TranslatePipe,
    NgxPermissionsModule.forRoot(),
  ],
  providers: [
    /** Hydration + HTTP transfer: reuse SSR GET responses on the client (avoids duplicate API/i18n fetches). */
    provideClientHydration(
      withHttpTransferCacheOptions({
        /** Admin APIs use Bearer auth; without this, cached SSR responses are skipped and the client refetches everything. */
        includeRequestsWithAuthHeaders: true,
      }),
    ),
    SharedService,
    AuthGuard,
    MfaEnforcementGuard,
    CrudService,
    ConstantService,
    CustomRouterService,
    TranslationService,
    /** XHR backend is Zone-patched; the fetch backend can complete without a CD cycle (UI stuck until the next click). */
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AdminAuthCredentialsInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: NgZoneHttpEnsureCdInterceptor,
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: adminPortalPermissionsInit,
      deps: [AdminPermissionBootstrapService],
      multi: true,
    },
    importProvidersFrom(
      TranslateModule.forRoot(
        {
          useDefaultLang: true,
          loader: {
            provide: TranslateLoader,
            useFactory: (httpNoInterceptorsService: HttpNoInterceptorsService) => {
              return createCustomTranslateLoader(httpNoInterceptorsService.getHttpClient());
            },
            deps: [HttpNoInterceptorsService]
          },
        }
      ),
    ),
  ],
  bootstrap: [AppComponent]
})

export class AppModule {
}
